import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import { formatDate } from "../../../../Utils/DateTimeUtils";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Create from "./Create";

// CSS for status styling
const statusStyles = `
  .status-tag {
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
  }
  
  .task-item {
    transition: background-color 0.3s ease;
    cursor: pointer;
  }
  
  .task-item:hover {
    background-color: #f0f0f0;
  }
  
  .task-item.highlight-row {
    animation: pulse-highlight 2s ease;
  }
  
  @keyframes pulse-highlight {
    0% { background-color: #fff8e1; }
    50% { background-color: #ffecb3; }
    100% { background-color: #fff8e1; }
  }
  
  .story-section {
    background-color: rgb(242 243 244);
    border-left: 3px solid #0747A6;
    border-radius: 5px;
    margin-bottom: 5px;
  }
  
  .task-description {
    margin-left: 60px !important;
    padding-left: 0 !important;
  }
`;

const Planning = () => {
  // Refs
  const toast = useRef(null);

  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Data states
  const [sprints, setSprints] = useState([]);
  const [activeSprint, setActiveSprint] = useState(null);
  const [planningSprintId, setPlanningSprintId] = useState(null);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [stories, setStories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsMap, setProjectsMap] = useState({});
  const [labels, setLabels] = useState({});
  const [developers, setDevelopers] = useState({});
  const [taskTypes, setTaskTypes] = useState({});

  // UI states
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [expandedStories, setExpandedStories] = useState({});
  const [individualTasksExpanded, setIndividualTasksExpanded] = useState(true);
  const [highlightedItems, setHighlightedItems] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch all necessary data
  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      // Fetch sprints, stories, non-completed tasks, projects, labels, developers, and task types in parallel
      const [sprintsResponse, storiesResponse, tasksResponse, projectsResponse, labelsResponse, developersResponse, taskTypesResponse] =
        await Promise.all([
          Http.get("/sprint"),
          Http.get("/story"),
          Http.get("/sprint/tasks-not-completed"), // Use new endpoint for non-completed tasks
          Http.get("/project"),
          Http.get("/label"),
          Http.get("/staff-department-wise/30"),
          Http.get("/task-type"),
        ]);

      console.log("tasksResponse", tasksResponse);

      // Set sprints, stories, and tasks
      setSprints(sprintsResponse.data.data || []);
      setStories(storiesResponse.data.data || []);
      setTasks(tasksResponse.data.data || []);

      // Set projects array
      setProjects(projectsResponse.data.data || []);

      // Create a map for project lookups
      const projectsMap = {};
      if (projectsResponse.data && projectsResponse.data.data) {
        projectsResponse.data.data.forEach((project) => {
          projectsMap[project.id] = project.title || project.name;
        });
      }
      setProjectsMap(projectsMap);

      // Create a map for label lookups
      const labelsMap = {};
      if (labelsResponse.data && labelsResponse.data.data) {
        labelsResponse.data.data.forEach((label) => {
          labelsMap[label.id] = {
            name: label.name,
            color: label.color,
          };
        });
      }
      setLabels(labelsMap);

      // Create a map for developer lookups
      const developersMap = {};
      if (Array.isArray(developersResponse.data)) {
        developersResponse.data.forEach((dev) => {
          const fullName = `${dev.first_name || ""} ${dev.last_name || ""}`.trim();
          developersMap[dev.id] = fullName || dev.name || `ID: ${dev.id}`;
        });
      }
      setDevelopers(developersMap);

      // Create a map for task type lookups
      const taskTypesMap = {};
      if (taskTypesResponse.data && taskTypesResponse.data.data) {
        taskTypesResponse.data.data.forEach((type) => {
          taskTypesMap[type.id] = {
            name: type.task_type,
            icon: type.icon || "pi pi-check-square",
            color: type.color || "#4CAF50",
          };
        });
      }
      setTaskTypes(taskTypesMap);

      // Find planning and active sprints - prioritize Planning, then Active
      const planningSprintData = sprintsResponse.data.data?.find((sprint) => sprint.status === "Planning");
      const activeSprintData = sprintsResponse.data.data?.find((sprint) => sprint.status === "Active");

      // Set planning sprint ID if exists
      if (planningSprintData) {
        setPlanningSprintId(planningSprintData.id);
      }

      // Set active sprint if exists
      if (activeSprintData) {
        setActiveSprint(activeSprintData);
      }

      // Preferred sprint selection order: Planning (if exists) → Active (if exists) → null
      // This ensures we always default to showing a relevant sprint if available
      const sprintToSelect = planningSprintData || activeSprintData || null;
      setSelectedSprint(sprintToSelect);

      // Initialize expanded state for stories
      const initialExpandedState = {};
      storiesResponse.data.data?.forEach((story) => {
        initialExpandedState[story.id] = true;
      });
      setExpandedStories(initialExpandedState);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data. Please refresh the page to try again.");
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for rendering
  const getUserName = (user) => {
    if (!user) return "N/A";
    return user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || `ID: ${user.id}`;
  };

  // Get all tasks that are not assigned to any sprint (backlog)
  const getBacklogTasks = (includeSprintTasks = false) => {
    return tasks.filter(
      (task) =>
        (includeSprintTasks || !task.sprint_id) &&
        (task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  // Get tasks for a specific story that are not in a sprint
  const getBacklogTasksForStory = (storyId) => {
    return tasks.filter(
      (task) =>
        task.story_id === storyId &&
        !task.sprint_id &&
        (task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  // Get stories that have backlog tasks
  const getStoriesWithBacklogTasks = () => {
    return stories.filter(
      (story) =>
        // Include stories that have tasks in backlog OR in the selected sprint
        (getBacklogTasksForStory(story.id).length > 0 || (selectedSprint && getSprintTasksForStory(story.id).length > 0)) &&
        (story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (story.description && story.description.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  // Get individual backlog tasks (not in a story)
  const getIndividualBacklogTasks = () => {
    return tasks.filter(
      (task) =>
        !task.story_id &&
        !task.sprint_id &&
        (task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  // Get tasks assigned to the selected sprint
  const getSprintTasks = () => {
    if (!selectedSprint) return [];
    return tasks.filter(
      (task) =>
        task.sprint_id === selectedSprint.id &&
        (task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  // Get story tasks that are assigned to the selected sprint
  const getSprintTasksForStory = (storyId) => {
    if (!selectedSprint) return [];
    return tasks.filter(
      (task) =>
        task.sprint_id === selectedSprint.id &&
        task.story_id === storyId &&
        (task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  // Get stories that have tasks in the selected sprint
  const getStoriesWithSprintTasks = () => {
    if (!selectedSprint) return [];
    return stories.filter(
      (story) =>
        getSprintTasksForStory(story.id).length > 0 &&
        (story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (story.description && story.description.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  // Get individual tasks that are assigned to the selected sprint
  const getIndividualSprintTasks = () => {
    if (!selectedSprint) return [];
    return tasks.filter(
      (task) =>
        task.sprint_id === selectedSprint.id &&
        !task.story_id &&
        (task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  // Toggle story expansion
  const toggleStoryExpansion = (storyId) => {
    setExpandedStories((prev) => ({
      ...prev,
      [storyId]: !prev[storyId],
    }));
  };

  // Toggle individual tasks expansion
  const toggleIndividualTasksExpansion = () => {
    setIndividualTasksExpanded(!individualTasksExpanded);
  };

  // Toggle description expansion
  const toggleDescriptionExpansion = (taskId, e) => {
    e.stopPropagation();
    setExpandedDescriptions((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  // Function to highlight an item for 2 seconds
  const highlightItem = (itemId, itemType) => {
    setHighlightedItems((prev) => ({
      ...prev,
      [`${itemType}-${itemId}`]: true,
    }));

    setTimeout(() => {
      setHighlightedItems((prev) => {
        const newState = { ...prev };
        delete newState[`${itemType}-${itemId}`];
        return newState;
      });
    }, 2000);
  };

  // Handle creating a new sprint
  const handleCreateSprint = () => {
    setCreateDialogVisible(true);
  };

  // Move tasks to sprint
  const moveTasksToSprint = async (taskIds) => {
    if (!selectedSprint || !selectedSprint.id) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a sprint first",
        life: 3000,
      });
      return;
    }

    try {
      const response = await Http.post(`/sprint/${selectedSprint.id}/assign-tasks`, {
        task_ids: taskIds,
      });

      // Update local state instead of fetching all data again
      setTasks((prevTasks) => {
        return prevTasks.map((task) => {
          // If this task is in the taskIds array, update its sprint_id
          if (taskIds.includes(task.id)) {
            return { ...task, sprint_id: selectedSprint.id };
          }
          return task;
        });
      });

      // Show success message
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: taskIds.length > 1 ? "Tasks moved to sprint successfully" : "Task moved to sprint successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error moving tasks to sprint:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to move tasks to sprint",
        life: 3000,
      });
    }
  };

  // Move all backlog tasks to selected sprint
  const moveAllTasksToSprint = async () => {
    const allBacklogTaskIds = getBacklogTasks().map((task) => task.id);

    if (allBacklogTaskIds.length === 0) {
      toast.current.show({
        severity: "info",
        summary: "Information",
        detail: "No backlog tasks to move",
        life: 3000,
      });
      return;
    }

    confirmDialog({
      message: `Are you sure you want to move all ${allBacklogTaskIds.length} backlog tasks to the sprint "${selectedSprint.name}"?`,
      header: "Confirm Move Tasks",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-primary",
      accept: () => moveTasksToSprint(allBacklogTaskIds),
    });
  };

  // Move a single task to selected sprint
  const moveTaskToSprint = async (taskId) => {
    moveTasksToSprint([taskId]);
  };

  // Render status with appropriate color
  const renderStatus = (status) => {
    let color = "";
    switch (status) {
      case "To Do":
        color = "#6c757d"; // gray
        break;
      case "In Progress":
        color = "#0d6efd"; // blue
        break;
      case "Review":
        color = "#fd7e14"; // orange
        break;
      case "Done":
        color = "#198754"; // green
        break;
      default:
        color = "#6c757d"; // default gray
    }

    return (
      <span
        className="status-tag"
        style={{
          backgroundColor: color,
          color: "#fff",
        }}
      >
        {status}
      </span>
    );
  };

  // Render label with color
  const renderLabel = (labelId) => {
    if (!labelId || !labels[labelId]) return null;
    const label = labels[labelId];

    return (
      <span
        className="badge"
        style={{
          backgroundColor: "transparent",
          color: label.color || "#ccc",
          border: `1px solid ${label.color || "#ccc"}`,
          padding: "3px 8px",
          borderRadius: "4px",
          fontSize: "11px",
          fontWeight: "500",
          height: "24px",
          lineHeight: "18px",
          textAlign: "center",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        {label.name}
      </span>
    );
  };

  // Render developer initials
  const renderDeveloperAvatar = (developerId) => {
    if (!developerId) return null;
    const name = developers[developerId] || "User";
    const initials = name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);

    return (
      <div
        className="avatar"
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          backgroundColor: "#0d6efd",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10px",
          fontWeight: "bold",
        }}
      >
        {initials}
      </div>
    );
  };

  // Render all developer avatars
  const renderDevelopers = (developers) => {
    if (!developers || developers.length === 0) return null;

    // Limit displayed developers to 3 with a +N indicator for additional ones
    const displayLimit = 3;
    const displayedDevelopers = developers.slice(0, displayLimit);
    const remainingCount = developers.length - displayLimit;

    return (
      <div className="d-flex align-items-center">
        {displayedDevelopers.map((dev, index) => (
          <div
            key={dev.id}
            className="developer-badge"
            style={{
              marginRight: "2px",
              position: "relative",
            }}
          >
            {renderDeveloperAvatar(dev.id)}
          </div>
        ))}

        {/* Show indicator for additional developers */}
        {remainingCount > 0 && (
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: "#6c757d",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "11px",
              fontWeight: "bold",
              marginLeft: "2px",
            }}
          >
            +{remainingCount}
          </div>
        )}
      </div>
    );
  };

  // Render backlog item (story or task)
  const renderBacklogItem = (item, itemType, indentLevel = 0) => {
    const isStory = itemType === "story";
    const hasSubtasks = isStory && getBacklogTasksForStory(item.id).length > 0;
    const hasSprintTasks = isStory && selectedSprint && getSprintTasksForStory(item.id).length > 0;
    const isDescriptionExpanded = expandedDescriptions[item.id] || false;
    const isHighlighted = highlightedItems[`${itemType}-${item.id}`] || false;
    const isInSelectedSprint = !isStory && selectedSprint && item.sprint_id === selectedSprint.id;

    // For stories, calculate status based on tasks
    const storyStatus = isStory ? "To Do" : null;

    // Get task type data if this is a task
    const taskType = !isStory && item.task_type_id && taskTypes[item.task_type_id] ? taskTypes[item.task_type_id] : null;

    return (
      <React.Fragment key={`${itemType}-${item.id}`}>
        <div
          className={`task-item ${isHighlighted ? "highlight-row" : ""} ${isInSelectedSprint ? "in-sprint" : ""}`}
          style={{
            padding: isStory ? "8px 12px" : "8px 12px", // Make more compact
            borderBottom: "1px solid #e9ecef",
            display: "flex",
            alignItems: "center",
            backgroundColor: isHighlighted
              ? "#fff8e1"
              : isInSelectedSprint
              ? "#e3f2fd" // Light blue background for tasks in sprint
              : isStory
              ? "rgb(242 243 244)"
              : "white",
            boxShadow: isStory ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
            marginLeft: `${indentLevel * 20}px`, // Reduced indent
            position: "relative",
            borderLeft: isStory
              ? "3px solid #0747A6"
              : isInSelectedSprint
              ? "3px solid #2196F3" // Blue left border for tasks in sprint
              : "none",
            borderRadius: "3px", // Smaller border radius
            marginBottom: "3px", // Reduced margin
          }}
          onClick={() => isStory && toggleStoryExpansion(item.id)}
        >
          {/* Toggle arrow and Type Indicator Column - Fixed width */}
          <div style={{ display: "flex", width: "40px", flexShrink: 0 }}>
            {" "}
            {/* Reduced width */}
            {/* Toggle arrow for stories */}
            {isStory && (
              <div style={{ width: "16px", marginRight: "4px" }}>
                {" "}
                {/* Reduced width and margin */}
                {hasSubtasks || hasSprintTasks ? (
                  <i
                    className={`pi ${expandedStories[item.id] ? "pi-chevron-down" : "pi-chevron-right"}`}
                    style={{ color: "#666", fontSize: "12px" }}
                  />
                ) : (
                  <i className="pi pi-minus" style={{ color: "#ccc", fontSize: "12px" }} />
                )}
              </div>
            )}
            {/* Item Type Indicator */}
            <div style={{ width: "16px", textAlign: "center" }}>
              {" "}
              {/* Reduced width */}
              {isStory ? (
                <i
                  className="pi pi-bookmark"
                  style={{
                    color: hasSubtasks || hasSprintTasks ? "#0747A6" : "#97A0AF",
                    fontSize: "12px", // Smaller font size
                  }}
                ></i>
              ) : (
                <i
                  className={taskType?.icon || "pi pi-check-square"}
                  style={{
                    color: isInSelectedSprint ? "#2196F3" : taskType?.color || "#4CAF50",
                    fontSize: "12px", // Smaller font size
                  }}
                ></i>
              )}
            </div>
          </div>

          {/* Item Title and Details Column - Flexible width */}
          <div style={{ flex: 1, minWidth: 0, paddingRight: "8px" }}>
            {" "}
            {/* Reduced padding */}
            <div
              style={{
                fontWeight: isStory ? "600" : "500",
                fontSize: isStory ? "13px" : "12px", // Smaller font sizes
                marginBottom: "2px", // Reduced margin
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.title}
              {!isStory && (
                <>
                  {taskType && (
                    <span
                      style={{
                        fontSize: "10px", // Smaller font size
                        marginLeft: "4px", // Reduced margin
                        color: isInSelectedSprint ? "#2196F3" : taskType.color,
                        border: `1px solid ${isInSelectedSprint ? "#2196F3" : taskType.color}`,
                        borderRadius: "3px",
                        padding: "0px 3px", // Reduced padding
                      }}
                    >
                      {taskType.name}
                    </span>
                  )}
                  {isInSelectedSprint && (
                    <span
                      style={{
                        fontSize: "10px", // Smaller font size
                        marginLeft: "4px", // Reduced margin
                        color: "#2196F3",
                        border: "1px solid #2196F3",
                        borderRadius: "3px",
                        padding: "0px 3px", // Reduced padding
                        backgroundColor: "rgba(33, 150, 243, 0.1)",
                      }}
                    >
                      In Sprint
                    </span>
                  )}
                </>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", fontSize: "10px", color: "#6c757d" }}>
              {" "}
              {/* Smaller font size */}
              {isStory ? (
                <>
                  <span style={{ marginRight: "8px" }}>
                    {" "}
                    {/* Reduced margin */}
                    <i className="pi pi-folder" style={{ marginRight: "2px", fontSize: "10px" }}></i> {/* Smaller icon */}
                    {projectsMap[item.project_id] || "N/A"}
                  </span>

                  {hasSprintTasks && (
                    <span style={{ marginRight: "8px", color: "#2196F3" }}>
                      <i className="pi pi-check-circle" style={{ marginRight: "2px", fontSize: "10px" }}></i>
                      {getSprintTasksForStory(item.id).length} in sprint
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span style={{ marginRight: "8px" }}>
                    {" "}
                    {/* Reduced margin */}
                    <i className="pi pi-calendar" style={{ marginRight: "2px", fontSize: "10px" }}></i> {/* Smaller icon */}
                    {formatDate(item.start_date)} - {formatDate(item.end_date)}
                  </span>
                </>
              )}
              {/* Description toggle for tasks */}
              {!isStory && item.description && (
                <div style={{ display: "flex", alignItems: "center", marginTop: "2px", fontSize: "10px", color: "#6c757d" }}>
                  {" "}
                  {/* Smaller font size */}
                  <span
                    style={{
                      cursor: "pointer",
                      color: isDescriptionExpanded ? "#0747A6" : "#6c757d",
                      textDecoration: "underline",
                      marginRight: "8px", // Reduced margin
                    }}
                    onClick={(e) => toggleDescriptionExpansion(item.id, e)}
                  >
                    <i
                      className={`pi ${isDescriptionExpanded ? "pi-chevron-up" : "pi-chevron-down"}`}
                      style={{ marginRight: "2px", fontSize: "10px" }}
                    ></i>{" "}
                    {/* Smaller icon */}
                    {isDescriptionExpanded ? "Hide details" : "Show details"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Status Column - Fixed width */}
          <div
            style={{
              width: "100px", // Reduced width
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!isStory ? renderStatus(item.status) : <div style={{ width: "60px" }}></div>} {/* Reduced width */}
          </div>

          {/* Label Column - Fixed width */}
          <div
            style={{
              width: "80px", // Reduced width
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {item.label_id ? renderLabel(item.label_id) : <div style={{ width: "50px" }}></div>} {/* Reduced width */}
          </div>

          {/* Developers Column - Fixed width */}
          <div
            style={{
              width: "80px", // Reduced width
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isStory && item.developer_id ? (
              renderDeveloperAvatar(item.developer_id)
            ) : !isStory && item.developers && item.developers.length > 0 ? (
              renderDevelopers(item.developers)
            ) : (
              <div style={{ width: "24px" }}></div>
            )}
          </div>

          {/* Actions Column - Fixed width */}
          <div
            style={{
              width: "140px", // Increased width for multiple buttons
              flexShrink: 0,
              display: "flex",
              gap: "4px",
              justifyContent: "flex-end",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {!isStory && !isInSelectedSprint && (
              <Button
                icon="pi pi-plus-circle"
                className="p-button-rounded p-button-success p-button-sm"
                style={{ width: "24px", height: "24px" }} // Smaller buttons
                disabled={!selectedSprint}
                onClick={() => moveTaskToSprint(item.id)}
                tooltip="Add to Sprint"
                tooltipOptions={{ position: "top" }}
              />
            )}
            {!isStory && isInSelectedSprint && selectedSprint && selectedSprint.status !== "Active" && (
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                style={{
                  width: "24px",
                  height: "24px",
                  color: "#e91e63",
                  border: "1px solid #f8bbd0",
                  backgroundColor: "#fce4ec",
                }}
                onClick={() => removeTaskFromSprint(item.id)}
                tooltip="Remove from Sprint"
                tooltipOptions={{ position: "top" }}
              />
            )}
            {isStory && hasSubtasks && (
              <Button
                icon="pi pi-plus-circle"
                className="p-button-text p-button-sm"
                style={{
                  width: "auto",
                  height: "24px",
                  color: "#4caf50",
                  border: "1px solid #c8e6c9",
                  backgroundColor: "#e8f5e9",
                  padding: "0 6px",
                }}
                disabled={!selectedSprint || getBacklogTasksForStory(item.id).length === 0}
                onClick={() => moveTasksToSprint(getBacklogTasksForStory(item.id).map((task) => task.id))}
                tooltip="Add Tasks to Sprint"
                tooltipOptions={{ position: "top" }}
              >
                <span style={{ fontSize: "10px", marginLeft: "2px", whiteSpace: "nowrap" }}>Add All</span>
              </Button>
            )}
            {isStory && hasSprintTasks && selectedSprint && selectedSprint.status !== "Active" && (
              <Button
                icon="pi pi-minus-circle"
                className="p-button-text p-button-sm"
                style={{
                  width: "auto",
                  height: "24px",
                  color: "#f44336",
                  border: "1px solid #ffcdd2",
                  backgroundColor: "#ffebee",
                  padding: "0 6px",
                  whiteSpace: "nowrap",
                }}
                onClick={() => removeTasksFromSprint(getSprintTasksForStory(item.id).map((task) => task.id))}
                tooltip="Remove All From Sprint"
                tooltipOptions={{ position: "top" }}
              >
                <span style={{ fontSize: "10px", marginLeft: "2px", whiteSpace: "nowrap" }}>Remove All</span>
              </Button>
            )}
          </div>
        </div>

        {/* Render backlog tasks for this story if expanded */}
        {isStory && expandedStories[item.id] && getBacklogTasksForStory(item.id).map((task) => renderBacklogItem(task, "task", 1))}

        {/* Render sprint tasks for this story if expanded */}
        {isStory && expandedStories[item.id] && getSprintTasksForStory(item.id).map((task) => renderBacklogItem(task, "task", 1))}

        {/* Show task description when expanded */}
        {!isStory && isDescriptionExpanded && item.description && (
          <div
            className="task-description"
            style={{
              padding: "8px 12px 8px 48px", // More compact
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e9ecef",
              marginLeft: `${indentLevel * 20}px`, // Reduced indent
              fontSize: "12px", // Smaller font size
              position: "relative",
              lineHeight: "1.3", // Tighter line height
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxWidth: "100%", overflowX: "auto" }}>
              <div style={{ marginBottom: "4px", fontWeight: "500" }}>Description:</div>
              <div dangerouslySetInnerHTML={{ __html: item.description }} />
            </div>
          </div>
        )}
      </React.Fragment>
    );
  };

  // Remove tasks from sprint and return to backlog
  const removeTasksFromSprint = async (taskIds) => {
    if (!selectedSprint) return;

    // Prevent task removal if sprint is Active
    if (selectedSprint.status === "Active") {
      toast.current.show({
        severity: "warn",
        summary: "Restricted Action",
        detail: "Tasks cannot be removed from an active sprint",
        life: 3000,
      });
      return;
    }

    try {
      const response = await Http.post(`/sprint/${selectedSprint.id}/remove-tasks`, {
        task_ids: taskIds,
      });

      // Update local state instead of fetching all data again
      setTasks((prevTasks) => {
        return prevTasks.map((task) => {
          // If this task is in the taskIds array, set its sprint_id to null
          if (taskIds.includes(task.id)) {
            return { ...task, sprint_id: null };
          }
          return task;
        });
      });

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: taskIds.length > 1 ? "Tasks removed from sprint" : "Task removed from sprint",
        life: 3000,
      });
    } catch (error) {
      console.error("Error removing tasks from sprint:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to remove tasks from sprint",
        life: 3000,
      });
    }
  };

  // Helper method for removing a single task from sprint
  const removeTaskFromSprint = (taskId) => {
    removeTasksFromSprint([taskId]);
  };

  // Handle start sprint functionality
  const handleStartSprint = async () => {
    if (!selectedSprint || selectedSprint.status !== "Planning") {
      toast.current.show({
        severity: "warn",
        summary: "Cannot Start Sprint",
        detail: "Only sprints in Planning status can be started",
        life: 3000,
      });
      return;
    }

    try {
      await Http.post(`/sprint/${selectedSprint.id}/start`, {});

      // Update the sprint status locally
      setSelectedSprint({
        ...selectedSprint,
        status: "Active",
      });

      // Update sprints list with the new status
      setSprints((prevSprints) => {
        return prevSprints.map((sprint) => {
          if (sprint.id === selectedSprint.id) {
            return { ...sprint, status: "Active" };
          }
          return sprint;
        });
      });

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Sprint started successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error starting sprint:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to start sprint",
        life: 3000,
      });
    }
  };

  return (
    <div>
      <IsLogin />
      <Toast ref={toast} />
      <ConfirmDialog />
      <style>{statusStyles}</style>

      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Sprint Planning</h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center">
              <span className="p-input-icon-left mr-3" style={{ width: "300px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search backlog..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
            </div>
          </div>
        </div>

        <div className="am-pagebody">
          {/* Sprint Selection Section - Redesigned for modern look */}
          <div className="card p-0 overflow-hidden">
            {loading ? (
              <div className="text-center p-5">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "1.5rem" }}></i>
                <p className="mt-2">Loading sprints...</p>
              </div>
            ) : (
              <div>
                {selectedSprint ? (
                  <div className="sprint-card">
                    {/* Sprint header with gradient background */}
                    <div
                      className="p-3"
                      style={{
                        background:
                          selectedSprint.status === "Planning"
                            ? "linear-gradient(135deg, #4b6cb7 0%, #182848 100%)"
                            : "linear-gradient(135deg, #1e8449 0%, #196f3d 100%)",
                        color: "white",
                        borderRadius: "4px 4px 0 0",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="m-0" style={{ fontSize: "1.3rem", fontWeight: "600" }}>
                          {selectedSprint.name}
                        </h5>
                        <Tag
                          value={selectedSprint.status}
                          severity={selectedSprint.status === "Planning" ? "info" : selectedSprint.status === "Active" ? "success" : "warning"}
                          className="px-2 py-1"
                          style={{ fontSize: "0.8rem", fontWeight: "500" }}
                        />
                      </div>
                      <div className="d-flex align-items-center mt-1" style={{ fontSize: "0.85rem" }}>
                        <span className="mr-3">
                          <i className="pi pi-calendar mr-1"></i>
                          {formatDate(selectedSprint.start_date)} - {formatDate(selectedSprint.end_date)}
                        </span>
                        {selectedSprint.goal && (
                          <span title={selectedSprint.goal} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <i className="pi pi-flag mr-1"></i>
                            {selectedSprint.goal.length > 40 ? selectedSprint.goal.substring(0, 40) + "..." : selectedSprint.goal}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Sprint details and actions */}
                    <div className="p-3 bg-white">
                      <div className="row align-items-center">
                        {/* Sprint metrics */}
                        <div className="col-md-8">
                          <div className="d-flex align-items-center">
                            {/* Tasks counter card */}
                            <div
                              className="d-flex flex-column align-items-center p-2 mr-3"
                              style={{
                                background: "#f8f9fa",
                                borderRadius: "6px",
                                minWidth: "90px",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                              }}
                            >
                              <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#3f51b5", lineHeight: "1.2" }}>
                                {getSprintTasks().length}
                              </span>
                              <span style={{ fontSize: "0.8rem", color: "#666" }}>Tasks</span>
                            </div>

                            {/* Status message */}
                            <div className="d-flex flex-column justify-content-center pl-1">
                              {selectedSprint.status === "Planning" ? (
                                <p className="mb-1" style={{ fontSize: "0.85rem" }}>
                                  <i className="pi pi-info-circle mr-1" style={{ color: "#3f51b5" }}></i>
                                  This sprint is in planning. Add tasks and start when ready.
                                </p>
                              ) : (
                                <p className="mb-1" style={{ fontSize: "0.85rem" }}>
                                  <i className="pi pi-info-circle mr-1" style={{ color: "#4caf50" }}></i>
                                  Active sprint: add tasks only, no removals allowed.
                                </p>
                              )}

                              {/* Sprint selector dropdown when we have multiple sprints */}
                              {sprints.filter((s) => s.status === "Planning" || s.status === "Active").length > 1 && (
                                <Dropdown
                                  value={selectedSprint}
                                  options={sprints.filter((s) => s.status === "Planning" || s.status === "Active")}
                                  onChange={(e) => setSelectedSprint(e.value)}
                                  optionLabel="name"
                                  placeholder="Select a sprint"
                                  style={{ width: "100%", height: "30px" }}
                                  className="p-inputtext-sm"
                                />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Action buttons */}
                        {/* iff sprint is active then dont show add all button */}
                        <div className="col-md-4 d-flex justify-content-end align-items-center">
                          {selectedSprint.status !== "Active" && (
                            <Button
                              label="Add All"
                              icon="pi pi-plus-circle"
                              className="p-button-outlined p-button-primary mr-2 p-button-sm"
                              onClick={moveAllTasksToSprint}
                              disabled={!selectedSprint || getBacklogTasks().length === 0}
                            />
                          )}

                          {selectedSprint.status === "Planning" && (
                            <Button label="Start Sprint" icon="pi pi-play" className="p-button-success p-button-sm" onClick={handleStartSprint} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-5 text-center bg-light">
                    <i className="pi pi-calendar-plus" style={{ fontSize: "3rem", color: "#ccc", marginBottom: "1rem" }}></i>
                    <h5>No Sprint Available</h5>
                    <p className="text-muted mb-3">No sprint in planning or active status found. Create a new sprint to begin planning.</p>
                    <Button label="Create Sprint" icon="pi pi-plus" className="p-button-success p-button-raised" onClick={handleCreateSprint} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Backlog Section */}
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-title mb-3">Backlog Tasks</h6>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center p-5">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                <p className="mt-2">Loading tasks...</p>
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center p-5">
                <i className="pi pi-inbox" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                <p className="mt-2">No tasks found.</p>
              </div>
            ) : (
              <div className="backlog-container" style={{ backgroundColor: "#f8f9fa", borderRadius: "6px", padding: "15px" }}>
                {/* Column headers */}
                <div
                  style={{
                    display: "flex",
                    padding: "6px 12px", // More compact
                    backgroundColor: "#e9ecef",
                    borderBottom: "1px solid #ced4da",
                    fontWeight: "600",
                    fontSize: "12px", // Smaller font size
                    color: "#495057",
                    marginBottom: "8px", // Reduced margin
                    borderRadius: "4px",
                  }}
                >
                  {/* Toggle and Title column */}
                  <div style={{ display: "flex", flex: 1, minWidth: 0 }}>
                    <div style={{ width: "40px" }}></div> {/* Reduced width */}
                    <div>Title</div>
                  </div>
                  {/* Status column */}
                  <div style={{ width: "80px", textAlign: "center" }}>Status</div> {/* Reduced width */}
                  {/* Label column */}
                  <div style={{ width: "80px", textAlign: "center" }}>Label</div> {/* Reduced width */}
                  {/* Developers column */}
                  <div style={{ width: "80px", textAlign: "center" }}>Devs</div> {/* Reduced width and shortened text */}
                  {/* Actions column */}
                  <div style={{ width: "140px", textAlign: "right" }}>Actions</div> {/* Increased width for two buttons */}
                </div>

                {/* Stories with backlog tasks */}
                {getStoriesWithBacklogTasks().map((story) => renderBacklogItem(story, "story"))}

                {/* Individual tasks section */}
                {tasks.filter((task) => !task.story_id).length > 0 && (
                  <>
                    <div
                      className="story-section"
                      style={{
                        padding: "10px 16px",
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={toggleIndividualTasksExpansion}
                    >
                      {/* Toggle arrow column - Fixed width */}
                      <div style={{ display: "flex", width: "40px", flexShrink: 0 }}>
                        <div style={{ width: "16px", marginRight: "4px" }}>
                          <i
                            className={`pi ${individualTasksExpanded ? "pi-chevron-down" : "pi-chevron-right"}`}
                            style={{ color: "#666", fontSize: "12px" }}
                          />
                        </div>
                        <div style={{ width: "16px", textAlign: "center" }}>
                          <i
                            className="pi pi-list"
                            style={{
                              color: "#0747A6",
                              fontSize: "12px",
                            }}
                          ></i>
                        </div>
                      </div>

                      {/* Title and Details Column - Flexible width */}
                      <div style={{ flex: 1, minWidth: 0, paddingRight: "8px" }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            marginBottom: "2px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Individual Tasks
                        </div>
                        <div style={{ display: "flex", alignItems: "center", fontSize: "10px", color: "#6c757d" }}>
                          <span style={{ marginRight: "8px" }}>
                            <i className="pi pi-check-square" style={{ marginRight: "2px", fontSize: "10px" }}></i>
                            {getIndividualBacklogTasks().length} in backlog
                          </span>
                          {selectedSprint && getIndividualSprintTasks().length > 0 && (
                            <span style={{ marginRight: "8px", color: "#2196F3" }}>
                              <i className="pi pi-check-circle" style={{ marginRight: "2px", fontSize: "10px" }}></i>
                              {getIndividualSprintTasks().length} in sprint
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Empty columns for alignment */}
                      <div style={{ width: "80px" }}></div>
                      <div style={{ width: "80px" }}></div>
                      <div style={{ width: "80px" }}></div>

                      {/* Actions Column */}
                      <div style={{ width: "140px", display: "flex", justifyContent: "flex-end", gap: "4px", flexWrap: "nowrap" }}>
                        {getIndividualBacklogTasks().length > 0 && (
                          <Button
                            icon="pi pi-plus-circle"
                            className="p-button-text p-button-sm"
                            style={{
                              width: "auto",
                              height: "24px",
                              color: "#4caf50",
                              border: "1px solid #c8e6c9",
                              backgroundColor: "#e8f5e9",
                              padding: "0 6px",
                              whiteSpace: "nowrap",
                            }}
                            disabled={!selectedSprint}
                            onClick={(e) => {
                              e.stopPropagation();
                              moveTasksToSprint(getIndividualBacklogTasks().map((task) => task.id));
                            }}
                            tooltip="Add All to Sprint"
                            tooltipOptions={{ position: "top" }}
                          >
                            <span style={{ fontSize: "10px", marginLeft: "2px", whiteSpace: "nowrap" }}>Add All</span>
                          </Button>
                        )}
                        {selectedSprint && getIndividualSprintTasks().length > 0 && selectedSprint.status !== "Active" && (
                          <Button
                            icon="pi pi-minus-circle"
                            className="p-button-text p-button-sm"
                            style={{
                              width: "auto",
                              height: "24px",
                              color: "#f44336",
                              border: "1px solid #ffcdd2",
                              backgroundColor: "#ffebee",
                              padding: "0 6px",
                              whiteSpace: "nowrap",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTasksFromSprint(getIndividualSprintTasks().map((task) => task.id));
                            }}
                            tooltip="Remove All From Sprint"
                            tooltipOptions={{ position: "top" }}
                          >
                            <span style={{ fontSize: "10px", marginLeft: "2px", whiteSpace: "nowrap" }}>Remove</span>
                          </Button>
                        )}
                      </div>
                    </div>
                    {individualTasksExpanded && tasks.filter((task) => !task.story_id).map((task) => renderBacklogItem(task, "task", 1))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Sprint Dialog */}
      <Dialog
        header="Create Sprint"
        visible={createDialogVisible}
        style={{ width: "50vw" }}
        onHide={() => setCreateDialogVisible(false)}
        dismissableMask
      >
        <Create
          setVisible={setCreateDialogVisible}
          fetchData={fetchData}
          projects={projects}
          onSuccess={() => {
            setCreateDialogVisible(false);
            fetchData();
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "Sprint created successfully",
              life: 3000,
            });
          }}
        />
      </Dialog>
    </div>
  );
};

export default Planning;
